import React, { useState } from 'react';
import { Button } from '../../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../../components/ui/dialog';
import { Upload } from 'lucide-react';
import { updateMaterialTraceability } from '../../../../../../redux/Project/materialSaga';
import customNotification from '../../../../../../components/ui/custom-notification';
import { MaterialData } from '../../../../types/materials';
import { Label } from '../../../../../../components/ui/label';
import { FileUpload } from '../../../../../../components/ui/FileUpload';

interface PipeDataUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload?: (files: FileList | null) => void;
    title?: string;
    acceptedTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    buttonText?: string;
    label?: string;
    description?: string;
    required?: boolean;
    material?: MaterialData;
    selectedMaterial?: MaterialData;
    selectedPipe?: any;
    project?: any;
    materialType?: string; // For customizing the modal based on material type
    selectedPipeSize?: any;
    refreshMaterials?: () => void;
}

export const PipeDataUploadModal: React.FC<PipeDataUploadModalProps> = ({
    isOpen,
    onClose,
    onUpload,
    title = 'Upload Pipe Data',
    acceptedTypes = '.json',
    multiple = true,
    maxFiles = 2,
    buttonText = 'Select Document',
    label = 'Document',
    description = 'Upload pipe data (json)',
    required = true,
    material,
    selectedMaterial,
    selectedPipe,
    project,
    materialType,
    selectedPipeSize,
    refreshMaterials
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);

    const handleMaterialFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setMaterialDocuments(prev => [...prev, ...fileArray]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setMaterialDocuments(prev => prev.filter((_, i) => i !== index));
    };


    const readJsonFile = (file: File): Promise<any> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const jsonData = JSON.parse(e.target?.result as string);
                    resolve(jsonData);
                } catch (error) {
                    reject(new Error('Invalid JSON file'));
                }
            };
            reader.onerror = () => reject(new Error('Error reading file'));
            reader.readAsText(file);
        });
    };

    const validateJsonStructure = (jsonData: any): boolean => {
        // Check if the JSON has the required structure
        return !!(jsonData.pipeinfo && jsonData.Data &&
            jsonData.pipeinfo.pipe_name &&
            jsonData.pipeinfo.pipe_end);
    };

    const extractPipeDataFromJson = (jsonData: any, fileName: string) => {
        // Validate JSON structure
        if (!validateJsonStructure(jsonData)) {
            throw new Error(`Invalid JSON structure in file ${fileName}. Missing required pipeinfo or Data sections.`);
        }

        // // Extract pipe information from JSON structure
        // const pipeInfo = jsonData.pipeinfo || {};
        // const measurementData = jsonData.Data || {};

        console.log(jsonData);

        return {
            fileName: fileName,
            pipeEnd: jsonData.pipeinfo.pipe_end,
            pipeName: jsonData.pipeinfo.pipe_name,
            data: jsonData
        };
    };

        const handleUpload = async () => {
            const currentMaterial = material || selectedMaterial;
            if (materialDocuments && materialDocuments.length > 0 && currentMaterial) {
                setIsUploading(true);
                try {
                    // Read and process JSON files
                    const jsonFilesData = await Promise.all(
                        materialDocuments.map(file => readJsonFile(file))
                    );

                    // Extract pipe data from each JSON file with validation
                    const pipeDataArray = jsonFilesData.map((jsonData, index) =>
                        extractPipeDataFromJson(jsonData, materialDocuments[index].name)
                    );

                    // Create pipe data mapping based on pipe_end (EndA, EndB, etc.)
                    const pipeDataMap: { [key: string]: any } = {};
                    const processedPipes: string[] = [];

                    pipeDataArray.forEach(pipeData => {
                        const endKey = pipeData.pipeEnd; // This will be "EndA", "EndB", etc.
                        if (endKey) {
                            if (pipeDataMap[endKey]) {
                                throw new Error(`Duplicate pipe end found: ${endKey}. Each pipe end should be unique.`);
                            }
                            pipeDataMap[endKey] = pipeData;
                            processedPipes.push(`${pipeData.pipeName} (${endKey})`);
                        } else {
                            throw new Error(`Invalid pipe end in file ${pipeData.fileName}. Pipe end must be specified.`);
                        }
                    });

                    // Log processed pipes for debugging
                    console.log('Processed pipe data:', processedPipes);

                    // Prepare material data for update
                    const materialData = [{
                        materialId: currentMaterial.materialId,
                        details: {
                            ...currentMaterial.details,
                            pipeExtraDetails: {
                                ...currentMaterial.details?.pipeExtraDetails,
                                pipeData: pipeDataMap
                            }
                        }
                    }];

                    // Convert string IDs back to numbers for API
                    const apiPayload = {
                        data: materialData
                    };
                    console.log(apiPayload);

                    // return;

                    const result = await updateMaterialTraceability(apiPayload);

                    if (result) {
                        customNotification.success(
                            'Upload successful',
                            `Pipe data has been uploaded and processed successfully. Processed: ${processedPipes.join(', ')}`
                        );
                        // Refresh materials list if callback is provided
                        if (refreshMaterials) {
                            refreshMaterials();
                        }
                        onClose();
                        setMaterialDocuments([]);
                    }
                } catch (error) {
                    console.error('Upload error:', error);
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                    customNotification.error(
                        'Upload failed',
                        `There was an error uploading the pipe data: ${errorMessage}`
                    );
                } finally {
                    setIsUploading(false);
                }
            }
        };

        const handleClose = () => {
            setMaterialDocuments([]);
            setIsUploading(false);
            onClose();
        };







        // Customize title based on material type
        const getModalTitle = () => {
            if (materialType) {
                return `Upload ${materialType} Pipe Data`;
            }
            return title;
        };

        return (
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent className="w-[90vw] max-w-[1200px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Upload className="h-5 w-5" />
                            {getModalTitle()}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Document Upload Section - Available for Pipe and Forge */}
                    <div className="grid  p-2 grid-cols-4 gap-4 min-w-0  max-h-[75vh] flex flex-col  overflow-y-auto    overflow-x-hidden">
                        <div className="space-y-2 col-span-4">
                            <Label>Upload Pipe Data</Label>
                            <p className="text-sm text-gray-600">
                                Upload the material documents.
                                <br />
                                <span className="text-xs text-gray-500">
                                    Allowed: json
                                    <br />
                                    Maximum file size: 100MB per file
                                </span>
                            </p>

                            <FileUpload
                                onFileSelect={handleMaterialFileSelect}
                                accept=".json"
                                multiple={true}
                                maxFiles={100}
                                buttonText="Upload Documents"
                                variant="outline"
                                size="lg"
                                onRemoveFile={handleRemoveFile}
                                selectedFiles={materialDocuments}
                                showFileList={materialDocuments.length > 0}
                                className="w-full"
                                showTags={false}
                                showAddButton={false}
                                maxHeight=""
                            />

                            {/* Debug: Show current documentFiles count */}
                            <div className="text-xs text-gray-500">
                                Current files: {materialDocuments.length} file(s)
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            className="flex items-center gap-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpload}
                            disabled={!materialDocuments || materialDocuments.length === 0 || isUploading}
                            loading={isUploading}
                            loadingText="Uploading..."
                            className="flex items-center gap-2"
                        >
                            <Upload className="h-4 w-4" />
                            Upload
                        </Button>
                    </DialogFooter>
                </DialogContent>


            </Dialog>
        );
    }; 